import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faSync, faEdit as faEditIcon } from '@fortawesome/free-solid-svg-icons';
import Layout from '../components/Layout';
import { useManageBot } from '../contexts/ManageBotContext';
import paymentPlanService from '../services/paymentPlanService';
import paymentCycleService from '../services/paymentCycleService';
import useConfirm from '../hooks/useConfirm';
import './PaymentPlans.css';

const PaymentPlans = () => {
  const { confirm, DialogComponent } = useConfirm();
  const navigate = useNavigate();
  const { botId } = useParams();
  const isInManageBot = useManageBot();

  const [paymentPlans, setPaymentPlans] = useState([]);
  const [paymentCycles, setPaymentCycles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    price_usd: '',
    price_eur: '',
    payment_cycle_id: '',
    charge_period: 'month',
    cycle: 1,
    message: '',
    pix_message: '',
    active: true,
    sort_order: 0,
    is_upsell_enabled: false,
    upsell_plan_id: '',
    upsell_message: '',
    upsell_price: '',
    upsell_expires_hours: 24,
    upsell_delay_minutes: 0,
    upsell_buttons: [],
  });

  useEffect(() => {
    if (botId) {
      loadData();
    } else {
      setError('Bot não selecionado. Por favor, selecione um bot primeiro.');
      setLoadingData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  const loadData = async () => {
    try {
      setLoadingData(true);
      setError('');
      const [plans, cycles] = await Promise.all([
        paymentPlanService.getAllPaymentPlans(botId),
        paymentCycleService.getAllCycles(),
      ]);
      setPaymentPlans(plans);
      setPaymentCycles(cycles);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value,
    });
    setError('');
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setFormData({
      title: '',
      price: '',
      price_usd: '',
      price_eur: '',
      payment_cycle_id: '',
      charge_period: 'month',
      cycle: 1,
      message: '',
      pix_message: '',
      active: true,
    sort_order: 0,
      is_upsell_enabled: false,
      upsell_plan_id: '',
      upsell_message: '',
      upsell_price: '',
      upsell_expires_hours: 24,
      upsell_delay_minutes: 0,
      upsell_buttons: [],
    });
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    const prices = plan.prices || {};
    setFormData({
      title: plan.title || '',
      price: plan.price || '',
      price_usd: prices.USD || '',
      price_eur: prices.EUR || '',
      payment_cycle_id: plan.payment_cycle_id || '',
      charge_period: plan.charge_period || 'month',
      cycle: plan.cycle || 1,
      message: plan.message || '',
      pix_message: plan.pix_message || '',
      active: plan.active !== undefined ? plan.active : true,
      sort_order: plan.sort_order ?? 0,
      is_upsell_enabled: plan.is_upsell_enabled || false,
      upsell_plan_id: plan.upsell_plan_id || '',
      upsell_message: plan.upsell_message || '',
      upsell_price: plan.upsell_price || '',
      upsell_expires_hours: plan.upsell_expires_hours || 24,
      upsell_delay_minutes: plan.upsell_delay_minutes || 0,
      upsell_buttons: Array.isArray(plan.upsell_buttons) ? plan.upsell_buttons : [],
    });
    setShowModal(true);
  };

  const handleDelete = async (planId) => {
    const confirmed = await confirm({
      message: 'Tem certeza que deseja remover este plano de pagamento?',
      type: 'warning',
    });

    if (!confirmed) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await paymentPlanService.deletePaymentPlan(planId, botId);
      setSuccess('Plano de pagamento removido com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao remover plano de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!botId) {
      setError('Bot não selecionado');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Build prices object for multi-currency
      const prices = {};
      const supportedCurrencies = ['BRL'];
      if (formData.price_usd && parseFloat(formData.price_usd) > 0) {
        prices.USD = parseFloat(formData.price_usd);
        supportedCurrencies.push('USD');
      }
      if (formData.price_eur && parseFloat(formData.price_eur) > 0) {
        prices.EUR = parseFloat(formData.price_eur);
        supportedCurrencies.push('EUR');
      }

      const planData = {
        ...formData,
        bot_id: parseInt(botId),
        price: parseFloat(formData.price),
        prices: Object.keys(prices).length > 0 ? prices : null,
        supported_currencies: supportedCurrencies.length > 1 ? supportedCurrencies : null,
        cycle: parseInt(formData.cycle),
        upsell_plan_id: formData.is_upsell_enabled && formData.upsell_plan_id ? parseInt(formData.upsell_plan_id) : null,
        upsell_price: formData.is_upsell_enabled && formData.upsell_price ? parseFloat(formData.upsell_price) : null,
        upsell_expires_hours: formData.is_upsell_enabled ? parseInt(formData.upsell_expires_hours) || 24 : 24,
        upsell_delay_minutes: formData.is_upsell_enabled ? parseInt(formData.upsell_delay_minutes) || 0 : 0,
        upsell_buttons: formData.is_upsell_enabled && Array.isArray(formData.upsell_buttons)
          ? formData.upsell_buttons.filter(b => b.title && b.url)
          : [],
        upsell_message: formData.is_upsell_enabled ? formData.upsell_message : null,
      };
      // Remove temp fields
      delete planData.price_usd;
      delete planData.price_eur;

      if (editingPlan) {
        await paymentPlanService.updatePaymentPlan(editingPlan.id, planData);
        setSuccess('Plano de pagamento atualizado com sucesso!');
      } else {
        await paymentPlanService.createPaymentPlan(planData);
        setSuccess('Plano de pagamento criado com sucesso!');
      }

      setTimeout(() => setSuccess(''), 3000);
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors || 'Erro ao salvar plano de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const calculateEarnings = () => {
    // Calcular ganhos do mês atual (implementação simplificada)
    // TODO: Implementar cálculo real baseado em transações
    return 0;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const getChargePeriodLabel = (period) => {
    const labels = {
      day: 'Dia',
      week: 'Semana',
      month: 'Mês',
      year: 'Ano',
    };
    return labels[period] || period;
  };

  const content = (
    <>
      <DialogComponent />
      <div className="payment-plans-page">
        {loadingData ? (
          <div className="loading-container">Carregando...</div>
        ) : !botId ? (
          <div className="error-container">
            <p>{error}</p>
            <button onClick={() => navigate('/')} className="btn btn-primary">
              Voltar ao Dashboard
            </button>
          </div>
        ) : (
          <>
            <div className="payment-plans-content">
              <div className="payment-plans-header">
                
                <div className="header-actions">
                  <button onClick={loadData} className="btn btn-primary radius-8 px-14 py-6 text-sm" disabled={loading}>
                    Atualizar
                  </button>
                  <button onClick={handleCreate} className="btn btn-primary radius-8 px-14 py-6 text-sm">
                    Criar Plano
                  </button>
                  <button onClick={() => {/* TODO: Implementar edição de mensagem PIX */}} className="btn btn-info radius-8 px-30 py-8 text-sm">
                    Mensagem Pix
                  </button>
                </div>
              </div>

              {error && <div className="alert alert-error">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              {/* Earnings Card */}
              <div className="earnings-card">
                <div className="earnings-icon">
                  <span>R$</span>
                </div>
                <div className="earnings-info">
                  <div className="earnings-amount">{formatPrice(calculateEarnings())}</div>
                  <div className="earnings-label">Ganhos com planos de pagamentos criados esse mês</div>
                </div>
              </div>


              {/* Payment Plans List */}
              <div className="payment-plans-section">
                <h2 className="section-title">Planos de pagamento ativos</h2>
                {paymentPlans.length === 0 ? (
                  <div className="empty-state">
                    <p>Nenhum plano de pagamento cadastrado ainda.</p>
                    <p>Clique em "Criar Plano" para começar.</p>
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table className="payment-plans-table">
                      <thead>
                        <tr>
                          <th>Título</th>
                          <th>Preço</th>
                          <th>Cobrança</th>
                          <th>Ciclo</th>
                          <th>Mensagem</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentPlans.map((plan) => (
                          <tr key={plan.id}>
                            <td>{plan.title}</td>
                            <td>
                              {formatPrice(plan.price)}
                              {plan.prices && Object.keys(plan.prices).length > 0 && (
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                  {Object.entries(plan.prices).map(([cur, val]) => `${cur}: ${val}`).join(' | ')}
                                </div>
                              )}
                            </td>
                            <td>{getChargePeriodLabel(plan.charge_period)}</td>
                            <td>{plan.cycle}</td>
                            <td className="message-cell">{plan.message || '-'}</td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  onClick={() => handleEdit(plan)}
                                  className="btn-icon btn-edit"
                                  title="Editar"
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                  onClick={() => handleDelete(plan.id)}
                                  className="btn-icon btn-delete"
                                  title="Remover"
                                  disabled={loading}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Modal */}
            {showModal && (
              <div className="modal-overlay" onClick={() => setShowModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>{editingPlan ? 'Editar Plano' : 'Criar Novo Plano'}</h2>
                    <button className="modal-close" onClick={() => setShowModal(false)}>
                      ×
                    </button>
                  </div>
                  <form className="modal-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label>Título *</label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        placeholder="Ex: Plano Mensal"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Preço (BRL) *</label>
                        <input
                          type="number"
                          name="price"
                          value={formData.price}
                          onChange={handleChange}
                          required
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>

                      <div className="form-group">
                        <label>Ciclo de Pagamento *</label>
                        <select
                          name="payment_cycle_id"
                          value={formData.payment_cycle_id}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Selecione...</option>
                          {paymentCycles.map((cycle) => (
                            <option key={cycle.id} value={cycle.id}>
                              {cycle.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Preço (USD)</label>
                        <input
                          type="number"
                          name="price_usd"
                          value={formData.price_usd}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                          placeholder="Opcional"
                        />
                      </div>

                      <div className="form-group">
                        <label>Preço (EUR)</label>
                        <input
                          type="number"
                          name="price_eur"
                          value={formData.price_eur}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                          placeholder="Opcional"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Período de Cobrança *</label>
                        <select
                          name="charge_period"
                          value={formData.charge_period}
                          onChange={handleChange}
                          required
                        >
                          <option value="day">Dia</option>
                          <option value="week">Semana</option>
                          <option value="month">Mês</option>
                          <option value="year">Ano</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Ciclo *</label>
                        <input
                          type="number"
                          name="cycle"
                          value={formData.cycle}
                          onChange={handleChange}
                          required
                          min="1"
                          placeholder="1"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Mensagem</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Mensagem que será exibida ao usuário"
                      />
                    </div>

                    <div className="form-group">
                      <label>Mensagem PIX</label>
                      <textarea
                        name="pix_message"
                        value={formData.pix_message}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Mensagem específica para pagamento via PIX"
                      />
                    </div>

                    <div className="form-group">
                      <label>Ordem de exibição (menor aparece primeiro)</label>
                      <input
                        type="number"
                        name="sort_order"
                        value={formData.sort_order ?? 0}
                        onChange={handleChange}
                        min="0"
                        placeholder="0"
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          name="active"
                          checked={formData.active}
                          onChange={handleChange}
                        />
                        Plano ativo
                      </label>
                    </div>

                    {/* Seção Upsell */}
                    <div className="upsell-section">
                      <h3 className="upsell-section-title">Upsell</h3>
                      <div className="form-group">
                        <label>
                          <input
                            type="checkbox"
                            name="is_upsell_enabled"
                            checked={formData.is_upsell_enabled}
                            onChange={handleChange}
                          />
                          Habilitar upsell após compra
                        </label>
                      </div>

                      {formData.is_upsell_enabled && (
                        <>
                          <div className="form-group">
                            <label>Mensagem de Upsell (título + texto do pagamento aprovado)</label>
                            <textarea
                              name="upsell_message"
                              value={formData.upsell_message}
                              onChange={handleChange}
                              rows="4"
                              placeholder="Ex: ✅ Pagamento aprovado! Aproveite nossa oferta exclusiva... (suporta HTML)"
                            />
                          </div>

                          <div className="form-group">
                            <label>Botões da oferta (produtos/links externos)</label>
                            {(formData.upsell_buttons || []).map((btn, idx) => (
                              <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <input
                                  type="text"
                                  value={btn.title || ''}
                                  onChange={(e) => {
                                    const arr = [...(formData.upsell_buttons || [])];
                                    arr[idx] = { ...arr[idx], title: e.target.value };
                                    setFormData({ ...formData, upsell_buttons: arr });
                                  }}
                                  placeholder="Título do botão (ex: 🔥 Ver oferta)"
                                  style={{ flex: 1 }}
                                />
                                <input
                                  type="url"
                                  value={btn.url || ''}
                                  onChange={(e) => {
                                    const arr = [...(formData.upsell_buttons || [])];
                                    arr[idx] = { ...arr[idx], url: e.target.value };
                                    setFormData({ ...formData, upsell_buttons: arr });
                                  }}
                                  placeholder="URL (https://t.me/outrobot ou site)"
                                  style={{ flex: 2 }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const arr = [...(formData.upsell_buttons || [])];
                                    arr.splice(idx, 1);
                                    setFormData({ ...formData, upsell_buttons: arr });
                                  }}
                                  className="btn btn-cancel"
                                  style={{ padding: '8px 12px' }}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const arr = [...(formData.upsell_buttons || []), { title: '', url: '' }];
                                setFormData({ ...formData, upsell_buttons: arr });
                              }}
                              className="btn btn-secondary"
                            >
                              + Adicionar botão
                            </button>
                            <small style={{ display: 'block', marginTop: '4px', color: '#6b7280' }}>
                              Vazio = usa plano interno selecionado abaixo.
                            </small>
                          </div>

                          <div className="form-group">
                            <label>Plano Interno (fallback - opcional)</label>
                            <select
                              name="upsell_plan_id"
                              value={formData.upsell_plan_id}
                              onChange={handleChange}
                            >
                              <option value="">— Sem plano interno —</option>
                              {paymentPlans
                                .filter(p => !editingPlan || p.id !== editingPlan.id)
                                .map((plan) => (
                                  <option key={plan.id} value={plan.id}>
                                    {plan.title} - {formatPrice(plan.price)}
                                  </option>
                                ))}
                            </select>
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Preço Especial (BRL)</label>
                              <input
                                type="number"
                                name="upsell_price"
                                value={formData.upsell_price}
                                onChange={handleChange}
                                min="0"
                                step="0.01"
                                placeholder="Deixe vazio para usar o preço original"
                              />
                            </div>
                            <div className="form-group">
                              <label>Validade da Oferta (horas)</label>
                              <input
                                type="number"
                                name="upsell_expires_hours"
                                value={formData.upsell_expires_hours}
                                onChange={handleChange}
                                min="1"
                                max="168"
                                placeholder="24"
                              />
                            </div>
                            <div className="form-group">
                              <label>Atraso antes de enviar (minutos)</label>
                              <input
                                type="number"
                                name="upsell_delay_minutes"
                                value={formData.upsell_delay_minutes}
                                onChange={handleChange}
                                min="0"
                                max="1440"
                                placeholder="0 = imediato"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => setShowModal(false)}
                      >
                        Cancelar
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Salvando...' : editingPlan ? 'Atualizar' : 'Criar'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  if (isInManageBot) {
    return content;
  }

  return <Layout>{content}</Layout>;
};

export default PaymentPlans;
